import {
  QueryClient,
  QueryKey,
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/vue-query'
import { ApiCallNotificationOptions } from '../api/notifications'
import { getApiCallNotificationCallbacks } from '../api/notifications'

type QueryKeyOrFn<TData, TInput = unknown> =
  | QueryKey
  | ((result: TData, input: TInput) => QueryKey)

export type CacheUpdateStrategy<TData, TInput = unknown> = {
  /** Set data for specified query keys from mutation response. */
  setDataFromResponseQueryKeys?: QueryKeyOrFn<TData, TInput>[]
  /** Invalidate specified query keys when the mutation succeeds. */
  invalidateQueryKeys?: QueryKeyOrFn<TData, TInput>[]
  /**
   * Invalidate specified query keys and await their completion when the mutation succeeds.
   * This is useful for triggering key re-fetches without which the mutation cannot
   * be considered complete.
   */
  invalidateAndAwaitQueryKeys?: QueryKeyOrFn<TData, TInput>[]
}

/**
 * Base options for creating standard mutation hooks.
 */
export interface BaseMutationOptions<TInput, TData> {
  mutationFn: (input: TInput) => Promise<TData>
  cacheStrategy?: CacheUpdateStrategy<TData, TInput>
  notifications?: ApiCallNotificationOptions
  onSuccess?: (data: TData, variables: TInput) => void
  // Any additional options to pass to useMutation
  useMutationOptions?: UseMutationOptions<TData, unknown, TInput>
}

/**
 * Helper to resolve a query key from either a static key or function.
 * @param keyOrFn
 * @param result
 * @param input
 */
function maybeUnwrapKey<TData, TInput>(
  keyOrFn: QueryKeyOrFn<TData, TInput>,
  result: TData,
  input: TInput
): QueryKey {
  return typeof keyOrFn === 'function' ? keyOrFn(result, input) : keyOrFn
}

/**
 * Helper to invalidate multiple queries based on provided keys or functions.
 * The invalidated promises are returned for optional awaiting.
 * @param client
 * @param result
 * @param input
 * @param keys
 */
function invalidateQueries<TData, TInput>(
  client: QueryClient,
  result: TData,
  input: TInput,
  keys?: QueryKeyOrFn<TData, TInput>[]
): Promise<void>[] {
  return (
    keys?.map((key) => {
      const queryKey = maybeUnwrapKey(key, result, input)
      return client.invalidateQueries({
        queryKey,
      })
    }) ?? []
  )
}

/**
 * Core factory for creating Vue Query mutation hooks with standard patterns.
 *
 * Handles notifications, cache updates, and error handling.
 * Used as foundation for specialized mutation factories.
 * @param options - Configuration for the mutation
 * @param options.mutationFn - API call function
 * @param options.cacheStrategy - How to update cache after mutation (can set data and/or invalidate multiple keys)
 * @param options.notifications - Notification config (resourceName, operation, errorType). Omit to disable notifications.
 * @param options.onSuccess - Optional callback to run after success (in addition to notifications)
 * @returns A hook function that returns a Vue Query mutation object with standard mutation methods plus mutateAsyncNoThrow
 * @example
 * // Basic usage with notifications
 * const useUpdateThing = setupBaseMutation({
 *   mutationFn: (data) => api.update({ body: data }),
 *   cacheStrategy: {
 *     invalidateQueryKeys: [(result) => ['things', result.parentId]],
 *   },
 *   notifications: {
 *     resourceName: 'thing',
 *     operation: NotificationActions.UPDATE,
 *   },
 * })
 *
 * // In component
 * const { mutate } = useUpdateThing()
 * mutate(data, {
 *   onSuccess: () => closeModal()
 * })
 */
export function setupBaseMutation<TInput, TData>(
  options: BaseMutationOptions<TInput, TData>
) {
  return function () {
    const queryClient = useQueryClient()

    const notificationCallbacks = options.notifications
      ? getApiCallNotificationCallbacks(options.notifications)
      : null

    const mutation = useMutation({
      ...options.useMutationOptions,
      mutationFn: options.mutationFn,
      onSuccess: async (result, variables) => {
        // Set data from response for specified query keys
        for (const key of options.cacheStrategy?.setDataFromResponseQueryKeys ??
          []) {
          const queryKey = maybeUnwrapKey(key, result, variables)
          queryClient.setQueryData(queryKey, result)
        }

        // Invalidate specified query keys

        // Kick off the awaited invalidations first
        const invalidatePromises = invalidateQueries(
          queryClient,
          result,
          variables,
          options.cacheStrategy?.invalidateAndAwaitQueryKeys
        )
        // Then start the non-awaited invalidations
        void invalidateQueries(
          queryClient,
          result,
          variables,
          options.cacheStrategy?.invalidateQueryKeys
        )

        await Promise.all(invalidatePromises)

        notificationCallbacks?.showSuccessNotification()
        options.onSuccess?.(result, variables)
      },
      onError: notificationCallbacks?.showErrorNotification,
    })

    return {
      ...mutation,
      /**
       * Non-throwing async variant. Returns data or undefined on failure.
       * Useful when you prefer explicit undefined over try/catch for error handling.
       * @param args - Mutation arguments
       */
      mutateAsyncNoThrow: async (
        ...args: Parameters<typeof mutation.mutateAsync>
      ): Promise<TData | undefined> => {
        try {
          const data = await mutation.mutateAsync(...args)
          return data
        } catch {
          return undefined
        }
      },
    }
  }
}
